import { forwardRef, useId } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useDialogPortalContainer } from '@/shared/ui/core/Popover';
import { useFormFieldContext } from '@/shared/ui/core/formFieldContext';
import {
  controlHeightClass,
  errorClass,
  fieldBorderClass,
  fieldFocusClass,
  hintClass,
  labelClass,
} from '@/shared/ui/core/styles';
import type { ControlSize, FieldTone, SemanticTone } from '@/shared/ui/core/types';

export type SelectOption = {
  value: string;
  label: string;
  /** Second line shown by SearchableSelect when set. */
  description?: string;
  /** Extra lines under `description` (SearchableSelect). */
  descriptions?: string[];
  /** Short tag drawn with a background beside the label. */
  badge?: string;
  /** Badge tone for SearchableSelect; default `info`. */
  badgeTone?: SemanticTone;
  /** SearchableSelect hides the row edit action when false. */
  canEdit?: boolean;
  disabled?: boolean;
};

export type SelectProps = {
  label?: string;
  hint?: string;
  error?: string;
  tone?: FieldTone;
  size?: ControlSize;
  required?: boolean;
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  id?: string;
  className?: string;
  /** Outer wrapper (default `w-full flex-col`). Use `w-auto` for dense chrome. */
  rootClassName?: string;
  /** When no visible `label`, set for a11y. */
  'aria-label'?: string;
};

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    tone,
    size = 'md',
    required = false,
    options,
    placeholder = 'Chọn…',
    value,
    defaultValue,
    onValueChange,
    disabled,
    name,
    id,
    className,
    rootClassName,
    'aria-label': ariaLabel,
  },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const formField = useFormFieldContext();
  const effectiveError = error || (formField.inFormField ? formField.error : undefined);
  const describedBy = effectiveError ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
  const resolvedTone: FieldTone = tone ?? (effectiveError ? 'error' : 'default');
  const shouldRenderError = Boolean(effectiveError && (!formField.inFormField || (!formField.error && Boolean(error))));
  /** Radix Select disallows empty-string values — treat as uncontrolled placeholder. */
  const resolvedValue = value === '' || value === undefined ? undefined : value;

  return (
    <div className={cn('flex w-full flex-col gap-1.5', rootClassName)}>
      {label ? (
        <label htmlFor={fieldId} className={cn(labelClass, 'flex items-center gap-1')}>
          <span>{label}</span>
          {required ? (
            <span className="text-danger font-bold text-sm leading-none" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <SelectPrimitive.Root
        value={resolvedValue}
        defaultValue={defaultValue === '' ? undefined : defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
        name={name}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          id={fieldId}
          aria-label={ariaLabel}
          aria-invalid={resolvedTone === 'error' || undefined}
          aria-describedby={describedBy}
          className={cn(
            'inline-flex w-full items-center justify-between rounded-input border bg-surface cursor-pointer overflow-hidden',
            size === 'sm' ? 'px-2 gap-1 text-caption' : 'px-3 gap-2 text-body',
            'text-left text-text outline-none transition-colors',
            '[&>span:first-child]:truncate [&>span:first-child]:min-w-0 [&>span:first-child]:flex-1 [&>span:first-child]:text-left',
            fieldFocusClass,
            'disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-disabled-fg',
            'data-[placeholder]:text-text-muted',
            controlHeightClass[size],
            fieldBorderClass[resolvedTone],
            className,
          )}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon asChild className="shrink-0 flex items-center">
            <ChevronDown className={cn(size === 'sm' ? 'size-3.5' : 'size-4', 'shrink-0 text-text-secondary')} aria-hidden />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal container={useDialogPortalContainer()}>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            className={cn(
              'z-[var(--z-dropdown)] min-w-[var(--radix-select-trigger-width)] overflow-hidden',
              'rounded-control border border-border bg-surface shadow-2',
            )}
          >
            <SelectPrimitive.Viewport className="max-h-60 overflow-y-auto p-1">
              {options.map((opt) => (
                <SelectPrimitive.Item
                  key={opt.value}
                  value={opt.value}
                  disabled={opt.disabled}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center justify-between gap-1.5 rounded-control py-1.5 px-2.5 text-[12px] text-text',
                    'outline-none data-[highlighted]:bg-primary-soft data-[highlighted]:text-primary',
                    'data-[disabled]:pointer-events-none data-[disabled]:text-text-disabled',
                  )}
                >
                  <SelectPrimitive.ItemText className="min-w-0 flex-1 truncate">{opt.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="shrink-0 inline-flex">
                    <Check className="size-3.5 text-primary" aria-hidden />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      {shouldRenderError ? (
        <p id={`${fieldId}-error`} className={errorClass} role="alert">
          {effectiveError}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className={hintClass}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});
